import { useEffect } from 'react';
import { Container, Stack, Typography } from '@mui/material';
import { logEvent } from 'firebase/analytics';

import PageTitle from '../../components/PageTitle/PageTitle';
import { PrivacyPolicyHelmet } from './MyHelmet';
import { analytics } from '../../config/firebase';

export default function PrivacyPolicy() {
  // Firebase analytics
  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_title: 'Privacy Policy',
      page_location: window.location.href,
      page_path: window.location.pathname,
    });
  }, []);
  return (
    <>
      <PrivacyPolicyHelmet />
      <PageTitle>
        <Stack
          component={'header'}
          gap={1}
          justifyContent="center"
          textAlign={'center'}
        >
          <Typography variant="h1" sx={{ fontSize: '40px', fontWeight: '700' }}>
            Privacy Policy
          </Typography>
          <Stack>
            <Typography
              variant="body1"
              sx={{
                fontSize: '18px',
                color: 'primary.neutral550',
              }}
            >
              Last Updated Mar 7th, 2024
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '14px',
                color: 'primary.neutral550',
              }}
            >
              This page doesn't have a translation yet. Stay tuned.
            </Typography>
          </Stack>
        </Stack>
      </PageTitle>
      <Container
        maxWidth="lg"
        sx={{
          pb: 4,
          pt: 2,
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        }}
      >
        <section>
          <p>
            This Privacy Policy describes how Slex ("we," "our," or "us")
            collects, uses, and discloses your personal information when you use
            our website www.slex.tv (the "Site") and the services offered
            through the Site (collectively, the "Services"). By accessing or
            using the Services, you agree to the terms of this Privacy Policy.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Information We Collect
          </Typography>
          <p>
            When you visit our website and contact us through the provided
            contact form, we collect personal information that you voluntarily
            provide, including your full name, email address, and optionally
            your phone number and message.
          </p>
          <p>
            We also collect your email address if you choose to subscribe to our
            newsletter.
          </p>
          <p>
            Additionally, we may collect information about your device and
            browsing activity on our website, including your IP address, browser
            type, pages visited, and other diagnostic data.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            How We Use Your Information
          </Typography>
          <p>
            We may use the information we collect for various purposes,
            including to provide and maintain our Services, to communicate with
            you, to send you marketing emails and news (if you have subscribed
            to our newsletter), to analyze how our Services are used, and to
            improve our Services.
          </p>
          <p>
            We may also use cookies and similar tracking technologies to track
            your activity on our website and store certain information. You can
            configure your browser to refuse all cookies or to indicate when a
            cookie is being sent, but please note that some parts of our website
            may not function properly if you do so.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Disclosure of Your Information
          </Typography>
          <p>
            We may share your personal information with third-party service
            providers who assist us in providing and improving our Services, as
            well as with other third parties for purposes such as analytics,
            advertising, and marketing.
          </p>
          <p>
            We may also disclose your personal information in response to legal
            requirements, to protect our rights or property, or to prevent
            illegal activities.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Data Retention
          </Typography>
          <p>
            We will retain your personal information only for as long as
            necessary to fulfill the purposes outlined in this Privacy Policy,
            unless a longer retention period is required or permitted by law.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Google Analytics with Firebase
          </Typography>
          <p>
            We may use Google Analytics with Firebase to collect and analyze
            information about how our website is used. Google Analytics with
            Firebase collects information such as your device's Internet
            Protocol address (e.g., IP address), browser type, browser version,
            the pages of our service that you visit, the time and date of your
            visit, and other diagnostic data. This information is used to
            improve our website and services. For more information on how Google
            Analytics with Firebase handles your data, please refer to Google's
            Privacy Policy.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Invisible reCAPTCHA
          </Typography>
          <p>
            We use invisible reCAPTCHA on our forms to help protect against spam
            and abuse while allowing legitimate users to submit their
            information without disruption.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Fraud Prevention
          </Typography>
          <p>
            We may collect certain information, including your IP address, to
            detect and prevent fraudulent activities, such as multiple attempts
            to claim our free trial offer. This information helps us ensure fair
            access to our services and protect against abuse.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            International Transfers
          </Typography>
          <p>
            Your personal information may be transferred to and processed in
            countries other than your own, where data protection laws may
            differ. By using our Services, you consent to the transfer of your
            information to countries outside of your country of residence.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Children's Privacy
          </Typography>
          <p>
            Our Services are not intended for children under the age of 13, and
            we do not knowingly collect personal information from children under
            13. If you are a parent or guardian and believe that your child has
            provided us with personal information, please contact us so that we
            can delete the information.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Changes to This Privacy Policy
          </Typography>
          <p>
            We may update this Privacy Policy from time to time, and any changes
            will be effective when posted. We will notify you of any material
            changes to this Privacy Policy by prominently displaying a notice on
            our website or by sending you an email.
          </p>
          <Typography variant={'h2'} fontSize={'18px'} fontWeight="600">
            Contact Us
          </Typography>
          <p>
            If you have any questions or concerns about this Privacy Policy or
            our privacy practices, please contact us at{' '}
            <a href="mailto:contact@slex.tv">contact@slex.tv</a>.
          </p>
        </section>
      </Container>
    </>
  );
}
